import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { colors } from "configs";
import { base, gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { lineHeight, rem } from "utils";

import { BadgeProps } from "./Badge.types";

export const Container = styled(Box)<
  Pick<BadgeProps, "color" | "variant" | "type" | "size" | "dot"> & {
    withCloseable?: boolean;
    withPrefix?: boolean;
    withSuffix?: boolean;
  }
>(
  ({
    color,
    variant,
    type,
    size,
    dot,
    withCloseable,
    withPrefix,
    withSuffix,
  }) => {
    const isRounded = type === "rounded";

    const selectedColor = color ? colors[color as never] : null;
    let styleBasedOnColor = {
      background: selectedColor ? selectedColor[50] : base.White,
      borderColor: selectedColor ? selectedColor[200] : gray[300],
      color: selectedColor ? selectedColor[700] : gray[700],
      svgIconColor: selectedColor ? selectedColor[500] : gray[500],
    };
    if (selectedColor) {
      switch (variant) {
        case "outlined": {
          styleBasedOnColor = {
            background: base.White,
            borderColor: selectedColor[600],
            color: selectedColor[700],
            svgIconColor: selectedColor[500],
          };
          break;
        }
        default:
          break;
      }
    }

    let styleBasedOnType = {};
    switch (type) {
      case "rounded": {
        styleBasedOnType = {
          borderRadius: "16px",
          borderWidth: "1.5px",
        };
        break;
      }
      default: {
        styleBasedOnType = {
          borderRadius: "6px",
          borderWidth: "1px",
        };
        break;
      }
    }

    let styleBasedOnSize = {};
    switch (size) {
      case "small": {
        styleBasedOnSize = {
          padding: isRounded ? "2px 8px" : "2px 6px",
          fontSize: rem(12),
          lineHeight: lineHeight(18),
        };
        break;
      }
      case "medium": {
        styleBasedOnSize = {
          padding: isRounded ? "2px 10px" : "2px 8px",
          fontSize: rem(14),
          lineHeight: lineHeight(20),
        };
        break;
      }
      case "large": {
        styleBasedOnSize = {
          padding: isRounded ? "4px 12px" : "4px 10px",
          fontSize: rem(14),
          lineHeight: lineHeight(20),
        };
        break;
      }
      default:
        break;
    }

    let styleBasedOnDot = {};
    if (dot) {
      const commonDotStyle = {
        content: "''",
        position: "absolute",
        backgroundColor: styleBasedOnColor.color,
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        top: "50%",
        transform: "translateY(-50%)",
      };

      switch (size) {
        case "small": {
          styleBasedOnDot = {
            ...{ "&:before": { ...commonDotStyle, left: "6px" } },
            paddingLeft: "18px",
          };
          break;
        }
        case "medium": {
          styleBasedOnDot = {
            ...{ "&:before": { ...commonDotStyle, left: "8px" } },
            paddingLeft: "22px",
          };
          break;
        }
        case "large": {
          styleBasedOnDot = {
            ...{ "&:before": { ...commonDotStyle, left: "10px" } },
            paddingLeft: "24px",
          };
          break;
        }
        default:
          break;
      }
    }

    let styleBasedOnWithPrefix = {};
    if (withPrefix) {
      switch (size) {
        case "small": {
          styleBasedOnWithPrefix = {
            paddingLeft: "6px",
          };
          break;
        }
        case "medium": {
          styleBasedOnWithPrefix = {
            paddingLeft: "8px",
          };
          break;
        }
        case "large": {
          styleBasedOnWithPrefix = {
            paddingLeft: "10px",
          };
          break;
        }
        default:
          break;
      }
    }

    let styleBasedOnWithSuffix = {};
    if (withSuffix) {
      switch (size) {
        case "small": {
          styleBasedOnWithSuffix = {
            paddingRight: "6px",
          };
          break;
        }
        case "medium": {
          styleBasedOnWithSuffix = {
            paddingRight: "6px",
          };
          break;
        }
        case "large": {
          styleBasedOnWithSuffix = {
            paddingRight: "10px",
          };
          break;
        }
        default:
          break;
      }
    }

    let styleBasedOnWithCloseable = {};
    if (withCloseable) {
      switch (size) {
        case "small": {
          styleBasedOnWithCloseable = {
            paddingRight: "3px",
          };
          break;
        }
        case "medium": {
          styleBasedOnWithCloseable = {
            paddingRight: "4px",
          };
          break;
        }
        case "large": {
          styleBasedOnWithCloseable = {
            paddingRight: "6px",
          };
          break;
        }
        default:
          break;
      }
    }

    return {
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      borderStyle: "solid",
      boxShadow: color ? undefined : shadows.xs,
      fontWeight: 500,

      "& .badge-suffix, & .badge-prefix": {
        "& .MuiIconButton-root": {
          marginLeft: "-2px",
          padding: "2px",
          color: styleBasedOnColor.svgIconColor,
        },
        "& .MuiSvgIcon-root": {
          fontSize: rem(12),
          color: styleBasedOnColor.svgIconColor,
        },
      },

      ...styleBasedOnColor,
      ...styleBasedOnType,
      ...styleBasedOnSize,
      ...styleBasedOnDot,
      ...styleBasedOnWithPrefix,
      ...styleBasedOnWithSuffix,
      ...styleBasedOnWithCloseable,
    };
  }
);

export const Prefix = styled(Box)(() => ({
  display: "inline-flex",
  marginRight: "4px",
}));

export const Suffix = styled(Box)(() => ({
  display: "inline-flex",
  marginLeft: "4px",
}));
